import React, { useMemo } from 'react';
import { useReferral } from '../../contexts/ReferralContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { User, Phone, Mail, MapPin, Calendar } from 'lucide-react';

export default function PatientDetailsStep() {
    const { patient, updatePatient } = useReferral();

    const handleChange = (field, value) => {
        updatePatient({ [field]: value });
    };

    // Calculate age from date of birth
    const calculatedAge = useMemo(() => {
        if (!patient.date_of_birth) return null;
        const today = new Date();
        const birthDate = new Date(patient.date_of_birth);
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        return age >= 0 ? age : null;
    }, [patient.date_of_birth]);

    return (
        <Card className="card-clinical">
            <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <User className="w-5 h-5 text-teal-600" />
                    Patient Details
                </CardTitle>
                <CardDescription>
                    Enter the patient's demographic and contact information
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
                {/* NHS Number */}
                <div className="space-y-2">
                    <Label htmlFor="nhs_number" className="label-clinical">NHS Number (Optional)</Label>
                    <Input
                        id="nhs_number"
                        placeholder="123 456 7890"
                        value={patient.nhs_number}
                        onChange={(e) => handleChange('nhs_number', e.target.value)}
                        className="input-clinical"
                        data-testid="patient-nhs-input"
                    />
                </div>

                {/* Name */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <Label htmlFor="first_name" className="label-clinical">First Name *</Label>
                        <Input
                            id="first_name"
                            placeholder="John"
                            value={patient.first_name}
                            onChange={(e) => handleChange('first_name', e.target.value)}
                            className="input-clinical"
                            data-testid="patient-firstname-input"
                            required
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="last_name" className="label-clinical">Last Name *</Label>
                        <Input
                            id="last_name"
                            placeholder="Smith"
                            value={patient.last_name}
                            onChange={(e) => handleChange('last_name', e.target.value)}
                            className="input-clinical"
                            data-testid="patient-lastname-input"
                            required
                        />
                    </div>
                </div>

                {/* DOB, Age, and Gender - Responsive layout with proper breakpoints */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2 min-w-0">
                        <Label htmlFor="dob" className="label-clinical flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-slate-400 flex-shrink-0" />
                            <span className="truncate">Date of Birth *</span>
                        </Label>
                        <Input
                            id="dob"
                            type="date"
                            value={patient.date_of_birth}
                            onChange={(e) => handleChange('date_of_birth', e.target.value)}
                            className="input-clinical w-full"
                            data-testid="patient-dob-input"
                            required
                        />
                    </div>
                    <div className="space-y-2 min-w-0">
                        <Label htmlFor="age" className="label-clinical">Age</Label>
                        <Input
                            id="age"
                            type="text"
                            value={calculatedAge !== null ? `${calculatedAge} years` : '—'}
                            className="input-clinical w-full bg-slate-50 text-slate-700 cursor-not-allowed dark:bg-slate-800 dark:text-slate-300"
                            data-testid="patient-age-display"
                            readOnly
                            disabled
                        />
                    </div>
                    <div className="space-y-2 min-w-0">
                        <Label htmlFor="gender" className="label-clinical">Gender *</Label>
                        <Select 
                            value={patient.gender} 
                            onValueChange={(value) => handleChange('gender', value)}
                        >
                            <SelectTrigger data-testid="patient-gender-select" className="input-clinical w-full">
                                <SelectValue placeholder="Select gender" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="male">Male</SelectItem>
                                <SelectItem value="female">Female</SelectItem>
                                <SelectItem value="other">Other</SelectItem>
                                <SelectItem value="prefer_not_to_say">Prefer not to say</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                {/* Contact Information */}
                <div className="pt-4 border-t border-slate-100">
                    <h4 className="text-sm font-semibold text-slate-700 mb-4">Contact Information</h4>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="phone" className="label-clinical flex items-center gap-2">
                                <Phone className="w-4 h-4 text-slate-400 flex-shrink-0" />
                                <span>Phone Number</span>
                            </Label>
                            <Input
                                id="phone"
                                type="tel"
                                placeholder="07123 456789"
                                value={patient.phone}
                                onChange={(e) => handleChange('phone', e.target.value)}
                                className="input-clinical"
                                data-testid="patient-phone-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="email" className="label-clinical flex items-center gap-2">
                                <Mail className="w-4 h-4 text-slate-400 flex-shrink-0" />
                                <span>Email Address</span>
                            </Label>
                            <Input
                                id="email"
                                type="email"
                                placeholder="patient@email.com"
                                value={patient.email}
                                onChange={(e) => handleChange('email', e.target.value)}
                                className="input-clinical"
                                data-testid="patient-email-input"
                            />
                        </div>
                    </div>
                </div>

                {/* Address */}
                <div className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="address" className="label-clinical flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0" />
                            <span>Address</span>
                        </Label>
                        <Input
                            id="address"
                            placeholder="123 High Street, London"
                            value={patient.address}
                            onChange={(e) => handleChange('address', e.target.value)}
                            className="input-clinical"
                            data-testid="patient-address-input"
                        />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="postcode" className="label-clinical">Postcode</Label>
                            <Input
                                id="postcode"
                                placeholder="SW1A 1AA"
                                value={patient.postcode}
                                onChange={(e) => handleChange('postcode', e.target.value)}
                                className="input-clinical"
                                data-testid="patient-postcode-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="preferred_contact" className="label-clinical">Preferred Contact Method</Label>
                            <Select 
                                value={patient.preferred_contact} 
                                onValueChange={(value) => handleChange('preferred_contact', value)}
                            >
                                <SelectTrigger data-testid="patient-contact-select">
                                    <SelectValue placeholder="Select method" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="phone">Phone</SelectItem>
                                    <SelectItem value="email">Email</SelectItem>
                                    <SelectItem value="post">Post</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}
